import { useState } from "react";
import { FiDownload, FiEye, FiFileText, FiGrid } from "react-icons/fi";
import { toast } from "react-toastify";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import { getReport, exportReport, REPORT_TYPES } from "../../services/reportApi";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue, toList } from "../../utils/orgFilters"; // PM-18: filters hold lists
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Button, DataTable, Banner, OrgFilters, StatTile, ChartFrame } from "../../components/common";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { axisProps, tooltipProps, CHART_SERIES } from "../../utils/chartTheme";
import { formatRatingScore } from "../../utils/ratingScale";
import { downloadCsv } from "../../utils/exportCsv";
import { downloadXlsx } from "../../utils/exportExcel";
import { VIEWS } from "../../services/navigation";
import { getErrorMessage } from "../../utils/errors";
import ReportsHeading from "./components/ReportsHeading"; // [NEW] performance direction and progress pace

const api = { getReport, exportReport };

// [NEW] PM-3: one layout for every report: grouped catalogue on the left, filters on top, summary tiles, one chart,
// then the detail table. The groups below decide where each report sits in the catalogue.
const REPORT_GROUPS = [
  { title: "Performance", keys: ["employee-performance", "department-performance", "rating-distribution", "trend"] },
  { title: "Progress", keys: ["completion", "pending-actions"] },
];

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const avg = (list) => (list.length ? list.reduce((a, b) => a + b, 0) / list.length : null);
const countBy = (rows, key) => Object.entries(rows.reduce((acc, r) => { const k = r[key] ?? "—"; acc[k] = (acc[k] || 0) + 1; return acc; }, {}))
  .map(([name, value]) => ({ name, value }));

// Summary tiles and chart data worked out from the rows of the report on screen.
function summarise(type, rows) {
  const tiles = [{ label: "Rows", value: rows.length, hint: "After filters" }];
  let chart = null;
  const finals = rows.map((r) => num(r.final_score)).filter((v) => v !== null);
  if (type === "employee-performance") {
    tiles.push({ label: "Rated", value: finals.length, hint: `of ${rows.length}` });
    tiles.push({ label: "Average score", value: formatRatingScore(avg(finals)), hint: "Final score" });
    chart = { title: "Employees by rating", data: countBy(rows.filter((r) => r.rating), "rating"), unit: "Employees" };
  } else if (type === "department-performance") {
    const scored = rows.filter((r) => num(r.average_score) !== null);
    const best = [...scored].sort((a, b) => num(b.average_score) - num(a.average_score))[0];
    tiles.push({ label: "Average score", value: formatRatingScore(avg(scored.map((r) => num(r.average_score)))), hint: "All departments" });
    if (best) tiles.push({ label: "Best department", value: best.department_name || best.department_id, hint: formatRatingScore(best.average_score) });
    chart = { title: "Average score by department", data: scored.map((r) => ({ name: r.department_name || r.department_id, value: num(r.average_score) })), unit: "Score" };
  } else if (type === "rating-distribution") {
    const total = rows.reduce((a, r) => a + (num(r.count) || 0), 0);
    tiles.push({ label: "Employees rated", value: total });
    chart = { title: "Rating distribution", data: rows.map((r) => ({ name: r.rating, value: num(r.count) || 0 })), unit: "Employees" };
  } else if (type === "trend") {
    chart = { title: "Average score by cycle", data: rows.map((r) => ({ name: r.cycle_id, value: num(r.average_score) })), unit: "Score" };
  } else if (type === "completion") {
    const released = rows.filter((r) => /released|acknowledged|closed/i.test(String(r.status))).length;
    tiles.push({ label: "Released", value: released, hint: `of ${rows.length}` });
    chart = { title: "Appraisals by stage", data: countBy(rows, "stage"), unit: "Employees" };
  } else if (type === "pending-actions") {
    chart = { title: "Pending actions by person", data: countBy(rows, "owner").sort((a, b) => b.value - a.value).slice(0, 10), unit: "Actions" };
  }
  return { tiles, chart: chart && chart.data.length ? chart : null };
}

export default function ReportsPage() {
  const { cycleId, cycle } = useCycle();
  const { activeView } = useRole();
  const isManagerView = activeView === VIEWS.MANAGER;
  const types = activeView === VIEWS.MANAGEMENT ? REPORT_TYPES.filter((r) => r.aggregate) : REPORT_TYPES;
  const [type, setType] = useState(types[0].key);
  const [serverExporting, setServerExporting] = useState(false);
  const [buildingBook, setBuildingBook] = useState(false);
  // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity. The server knows the department; the rest is
  // applied here to every report row that belongs to an employee (aggregate reports such as Department performance
  // and Cycle trend have no employee rows, so only the department filter can narrow them).
  const org = useOrgFilters();
  // [UPDATED] PM-18: the server takes one department; with several departments the rows are filtered here instead.
  const departmentId = singleValue(org.filters.departmentId);
  const needsBrowserExport = Object.entries(org.filters).some(([k, v]) => toList(v).length > 0 && (k !== "departmentId" || !departmentId));
  const applyOrg = (list = []) => list.filter((r) => !("employee_id" in r) || org.matches(r.employee_id));
  const { data, loading, error } = useAsync(() => api.getReport(cycleId, type, { departmentId }), [cycleId, type, departmentId], { skip: !cycleId });

  const current = types.find((r) => r.key === type) || types[0];
  const rows = applyOrg(data?.rows).map((r, i) => ({ ...r, _key: i }));
  const scopeLabel = org.active ? " (filtered)" : "";
  const summary = summarise(type, rows); // [NEW] PM-3 tiles + chart

  const exportOfficial = async () => {
    setServerExporting(true);
    try {
      const { data: file } = await api.exportReport(cycleId, type, { departmentId });
      toast.success(`${file.fileName} downloaded (${file.row_count} rows).`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.info("The official server export is for HR only, so the file was built in your browser instead.");
        exportExcel();
      } else {
        toast.error(`Export failed: ${getErrorMessage(err)}`);
      }
    } finally {
      setServerExporting(false);
    }
  };

  const exportExcel = () => {
    downloadXlsx(`${type}-${cycle?.name}`, [
      {
        name: current.label,
        title: `${current.label} — ${cycle?.name}${scopeLabel}`,
        columns: data.columns,
        rows,
      },
    ]);
    toast.success(`${current.label} exported to Excel.`);
  };

  const exportWorkbook = async () => {
    setBuildingBook(true);
    try {
      const sheets = await Promise.all(
        types.map(async (report) => {
          const res = await api.getReport(cycleId, report.key, { departmentId });
          return {
            name: report.label,
            title: `${report.label} — ${cycle?.name}${scopeLabel}`,
            columns: res.data.columns,
            rows: applyOrg(res.data.rows),
          };
        })
      );
      downloadXlsx(`performance-reports-${cycle?.name}`, sheets);
      toast.success(`All ${sheets.length} reports exported to one Excel workbook.`);
    } catch (err) {
      toast.error(`Couldn't build the workbook: ${getErrorMessage(err)}`);
    } finally {
      setBuildingBook(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title={isManagerView ? "Team reports" : "Reports"}
        subtitle={isManagerView ? `${cycle?.name}. Reports about the people you review.` : `${cycle?.name}. Download any report as Excel or CSV.`}
        actions={
          <>
            {activeView === VIEWS.MANAGEMENT && <span className="readonly-chip"><FiEye size={14} aria-hidden="true" /> Read-only</span>}
            <Button variant="ghost" icon={FiFileText} disabled={!rows.length} onClick={() => downloadCsv(`${type}-${cycle?.name}`, data.columns, rows)}>CSV</Button>
            <Button variant="secondary" icon={FiGrid} loading={buildingBook} onClick={exportWorkbook}>All reports, one workbook</Button>
            <Button icon={FiDownload} disabled={!rows.length} loading={serverExporting} onClick={isManagerView || needsBrowserExport ? exportExcel : exportOfficial}>Export Excel</Button>
          </>
        }
      />
      <ReportsHeading cycle={cycle} />
      <div className="report-layout">
        <nav className="report-nav" aria-label="Report types">
          {REPORT_GROUPS.map((g) => {
            const list = types.filter((r) => g.keys.includes(r.key));
            if (!list.length) return null;
            return (
              <div key={g.title} className="report-nav-group">
                <p className="report-nav-heading">{g.title}</p>
                {list.map((r) => (
                  <button key={r.key} type="button" className={`report-nav-item ${type === r.key ? "is-active" : ""}`} onClick={() => setType(r.key)}>
                    {r.label}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="stack">
          <div className="filter-bar">
            <OrgFilters org={org} />
            <span className="muted">{rows.length} rows</span>
          </div>
          {current.aggregate && needsBrowserExport && (
            <Banner tone="neutral" title="This report is a summary">Business Unit, Location, Cost Centre and Legal Entity apply to reports that list people. Summary reports can only be narrowed by Department.</Banner>
          )}
          {error && <Banner tone="danger" title="Couldn't load the report">{error}</Banner>}
          {data && (
            <div className="stat-grid">
              {summary.tiles.map((t) => <StatTile key={t.label} label={t.label} value={t.value} hint={t.hint} />)}
            </div>
          )}
          {summary.chart && (
            <Card title={summary.chart.title}>
              <ChartFrame label={summary.chart.title} summary={summary.chart.data.map((d) => ({ label: d.name, value: d.value }))}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={summary.chart.data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7F0" />
                    <XAxis dataKey="name" {...axisProps} interval={0} />
                    <YAxis {...axisProps} allowDecimals={summary.chart.unit === "Score"} />
                    <Tooltip {...tooltipProps} formatter={(v) => [summary.chart.unit === "Score" ? formatRatingScore(v) : v, summary.chart.unit]} />
                    <Bar dataKey="value" fill={CHART_SERIES[0]} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartFrame>
            </Card>
          )}
          <Card title={`${current.label}: details`} padded={false}>
            <DataTable exportable={false}
              loading={loading}
              rowKey="_key"
              rows={rows}
              pageSize={12}
              emptyTitle="No data for this report"
              columns={(data?.columns || []).map((c) => ({ ...c }))}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
